import classNames from "classnames";
import { ComponentProps, HTMLProps, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Disabler } from "../disabler";
import useOutsideAlerter from "../hooks/use_outside_alerter";
import { WithTooltipPortaled } from "../tooltips/with_tooltip_portaled";
import { WithDropdownBase, WithDropdownBaseRef } from "./with_dropdown_base";

type DropdownItemCommon = {
	icon?: JSX.Element;
	label: string;
	close_on_click?: boolean;
	is_warning?: boolean;
	disabled_state?: {
		is_disabled: boolean;
		tooltip: string;
	};
};

export type DropdownItem =
	| (DropdownItemCommon & {
			type: "link";
			props: ComponentProps<typeof Link>;
	  })
	| (DropdownItemCommon & {
			type: "button";
			props: HTMLProps<HTMLButtonElement>;
	  })
	| (DropdownItemCommon & {
			type: "a";
			props: HTMLProps<HTMLAnchorElement>;
	  })
	| (DropdownItemCommon & {
			type: "dropdown";
			props: Omit<ComponentProps<typeof WithDropdownBase>, "children">;
	  });

export const DropdownItemToElementGenerator = (
	Element: (props: any) => JSX.Element
): Record<DropdownItem["type"], (item) => JSX.Element> => {
	return {
		a: (item) => (
			<a
				{...item.props}
				herf={item.disabled_state?.is_disabled ? "#" : item.props.href}
				className={classNames({
					"text-gray-400 hover:text-gray-400":
						item.disabled_state?.is_disabled,
					"hover:text-sesh-primary":
						!item.disabled_state?.is_disabled,
				})}
				disabled={item.disabled_state?.is_disabled}
			>
				<Element {...item} />
			</a>
		),
		button: (item) => (
			<button {...item.props} disabled={item.disabled_state?.is_disabled}>
				<Element {...item} />
			</button>
		),
		link: (item) => (
			<Link
				{...item.props}
				to={item.disabled_state?.is_disabled ? "#" : item.props.to}
				className={classNames("hello", {
					"text-gray-400 hover:text-gray-400":
						item.disabled_state?.is_disabled,
					"hover:text-sesh-primary":
						!item.disabled_state?.is_disabled,
				})}
			>
				<Element {...item} />
			</Link>
		),
		dropdown: (item) => (
			<WithDropdownBase {...item.props}>
				<Element {...item} />
			</WithDropdownBase>
		),
	};
};

export const DropdownItemToElementWithoutLabel = (color: string) =>
	DropdownItemToElementGenerator((props: DropdownItem) => (
		<WithTooltipPortaled
			disable_click
			tooltip={
				props.disabled_state?.is_disabled
					? props.disabled_state?.tooltip ?? props.label
					: props.label
			}
			style={"Info"}
		>
			<div
				style={{
					color: color,
				}}
				className={classNames("p-2", {
					"cursor-not-allowed opacity-40":
						props.disabled_state?.is_disabled,
					"opacity-100 hover:opacity-60":
						!props.disabled_state?.is_disabled,
				})}
			>
				{props.icon}
			</div>
		</WithTooltipPortaled>
	));
export const DropdownItemToElementWithLabel =
	DropdownItemToElementGenerator(DropdownItemLabel);

function DropdownItemLabel(props: DropdownItem) {
	return (
		<span
			className={classNames("flex gap-2 px-4 py-2 sm:py-1", {
				"text-warning": props.is_warning,
			})}
		>
			{props.icon && (
				<span className="flex w-5 items-center justify-center">
					{props.icon}
				</span>
			)}
			<span>{props.label}</span>
		</span>
	);
}

export function WithDropdownImproved(props: {
	children: React.ReactNode;
	items: (DropdownItem | { type: "divider" })[];
	alignment?: "left" | "right" | "auto" | "middle";
}) {
	const dropdownRef = useRef<WithDropdownBaseRef>(null);

	const handleItemClick = (item: DropdownItem) => {
		if (item.close_on_click) {
			dropdownRef.current?.close();
		}
	};

	return (
		<WithDropdownBase
			ref={dropdownRef}
			x_alignment={props.alignment}
			y_alignment="bottom"
			dropdown_element={
				<div className="w-fit rounded-sm border border-sesh-dark bg-base-dark !p-1 text-sm text-sesh-primary shadow-md">
					{props.items.map((val, index) => {
						if (val.type === "divider") {
							return (
								<hr
									key={index}
									className="my-1 h-[1px] border-none bg-sesh-primary/25"
								></hr>
							);
						}

						const result = (
							<Disabler
								key={index}
								disabled={val.disabled_state?.is_disabled}
							>
								<div
									className="sesh-dropdown-item w-full whitespace-nowrap font-semibold hover:bg-base-light"
									onClick={() => handleItemClick(val)}
								>
									{DropdownItemToElementWithLabel[val.type](
										val
									)}
								</div>
							</Disabler>
						);

						if (val.disabled_state?.is_disabled) {
							return (
								<WithTooltipPortaled
									key={index}
									disable_click
									style="Info"
									tooltip={val.disabled_state.tooltip}
								>
									{result}
								</WithTooltipPortaled>
							);
						}

						return result;
					})}
				</div>
			}
		>
			{props.children}
		</WithDropdownBase>
	);
}
